import { config } from "../config";
import { callGateway, type CallerIdentity } from "./gateway";
import { ANKH_SELLER } from "./ankhCustomers";
import type { PlatformTenant } from "./platform";
import { bandFor } from "../domain/tiers";

/**
 * Platform › Tenants › Plan (owner 2026-10-25): which io.ankhos plan a tenant
 * is on, and putting it on one. radaccess v1.165 (handler/tenantplan.go),
 * io.ankhos only, Ankh customer chain (admin + billing write, viewer read).
 *
 *   GET  /billing/io.ankhos/accounts/{acct}/plan        tenant, held plans, offered plans, count
 *   POST /billing/io.ankhos/accounts/{acct}/plan        { componentid }
 *   POST /billing/io.ankhos/accounts/{acct}/plan/end    { subcomponentid }
 *
 * 🔑 The first period IS the trial and no invoice is raised for it: the
 * gateway sets the dates, the console only shows them.
 */

const base = `/billing/${ANKH_SELLER}/accounts`;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

function acct(id: string): string {
  if (!UUID.test(id)) throw new Error(`Not an account id: ${id}`);
  return id;
}

export interface AnkhPlan {
  componentid: string;
  name: string;
  price: number;
  gstpercent: number;
  product: "ankhos" | "cloud";
  tier: string;
}

export interface HeldPlan {
  subscriptionid: string;
  subcomponentid: string;
  componentid: string;
  name: string;
  price: number;
  gstpercent: number;
  status: string;
  periodstart: string;
  periodend: string;
  nextrenewal: string;
  invoiced: boolean;
  billinginterval: string;
}

export interface TenantPlanView {
  tenant: PlatformTenant;
  product: "ankhos" | "cloud" | "";
  activesubscribers?: number;
  trialdays: number;
  accountopen: boolean;
  held: HeldPlan[];
  plans: AnkhPlan[];
  /** radbilling's estimate for each held ARREARS row (owner 2026-10-27). */
  monthtodate?: MonthToDate[];
  monthtodateerror?: string;
}

/** An operator's arrears month so far: what the month-end run would bill now. */
export interface MonthToDate {
  subcomponentid: string;
  plan: string;
  month: string; // YYYY-MM, IST
  window?: string;
  readings: number;
  averagesubscribers?: number;
  roundedaverage?: number;
  band?: string;
  bandcomponentid?: string;
  projectedprice?: number; // before GST, pro-rata for the billable window
  test?: boolean;
  reason?: string;
}

function monthName(ym: string): string {
  const [y, m] = ym.split("-").map(Number);
  if (!y || !m) return ym;
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleString("en-IN", { month: "long", year: "numeric", timeZone: "UTC" });
}

/** One line for the Plan section: the month so far, or why there is none. */
export function monthToDateText(e: MonthToDate): string {
  const month = monthName(e.month);
  if (!e.band || e.projectedprice === undefined || e.averagesubscribers === undefined) {
    return `${month} so far: ${e.reason ?? "no estimate yet"}.`;
  }
  const price = e.projectedprice.toLocaleString("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 });
  const readings = e.readings === 1 ? "1 daily reading" : `${e.readings} daily readings`;
  let s =
    `${month} so far: average ${e.averagesubscribers.toFixed(1)} active subscribers (${readings}) → ${e.band}, ` +
    `about ${price} + GST. An estimate: the invoice is drafted from the whole month's average when ${month} ends.`;
  if (e.test) s += " Test tenant: never invoiced.";
  return s;
}

export async function getTenantPlan(identity: CallerIdentity, accountid: string, signal?: AbortSignal): Promise<TenantPlanView> {
  const r = await callGateway<TenantPlanView>(identity, config().gatewayBase, `${base}/${acct(accountid)}/plan`, signal ? { signal } : {});
  return { ...r.data, held: r.data.held ?? [], plans: r.data.plans ?? [] };
}

export async function subscribeTenantPlan(
  identity: CallerIdentity,
  accountid: string,
  componentid: string,
): Promise<{ subscriptionid: string; plan: string; periodstart: string; periodend: string; note: string }> {
  if (!UUID.test(componentid)) throw new Error("Choose a plan");
  const r = await callGateway<{ subscriptionid: string; plan: string; periodstart: string; periodend: string; note: string }>(
    identity,
    config().gatewayBase,
    `${base}/${acct(accountid)}/plan`,
    { method: "POST", body: { componentid } },
  );
  return r.data;
}

export async function endTenantPlan(identity: CallerIdentity, accountid: string, subcomponentid: string): Promise<void> {
  if (!UUID.test(subcomponentid)) throw new Error("Not a plan");
  await callGateway(identity, config().gatewayBase, `${base}/${acct(accountid)}/plan/end`, {
    method: "POST",
    body: { subcomponentid },
  });
}

/**
 * The plan to preselect. An operator's band is DERIVED from its active
 * subscriber count (catalog ankhos-tiers-v2), so the screen picks it and says
 * why; a cloud tenant chooses, so nothing is preselected.
 */
export function suggestedPlan(v: Pick<TenantPlanView, "product" | "activesubscribers" | "plans">): AnkhPlan | undefined {
  if (v.product !== "ankhos" || v.activesubscribers === undefined) return undefined;
  const tier = bandFor(v.activesubscribers).tier;
  return v.plans.find((p) => p.tier === tier);
}

/** The monthly total with GST, as the invoice rounds it: nearest rupee (owner 2026-10-30). */
export function withGst(price: number, gstpercent: number): number {
  const half = Math.round((price * gstpercent) / 2) / 100;
  return Math.round(Math.round((price + 2 * half) * 100) / 100);
}
